import {
  LOGO_B64,
  SIG_B64,
  COMPANY,
  C_RED,
  C_DARK,
  C_GREY_TEXT,
  C_GREY_BG,
  C_WHITE,
} from "./generateInspectionPDF";

const C_NAVY = [30, 58, 95];
const C_AMBER = [245, 158, 11];

export async function generateRectificationQuotePDF(quote, signaturesMap = {}, opts = {}) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const pageH = 297;
  const margin = 15;
  const contentW = pageW - margin * 2;
  let y = margin;
  let pageNum = 1;

  const fmt2 = (n) => Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  function addPage() {
    doc.addPage();
    pageNum++;
    y = margin;
    drawPageFooter();
  }

  function drawPageFooter() {
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text(`Page ${pageNum}`, pageW / 2, pageH - 7, { align: "center" });
  }

  function checkBreak(needed) {
    if (y + needed > pageH - 18) addPage();
  }

  // ── HEADER ───────────────────────────────────────────────────────────────
  const logoW = 38;
  const logoH = logoW * (211 / 500);
  doc.addImage("data:image/jpeg;base64," + LOGO_B64, "JPEG", margin, y, logoW, logoH);

  const cx = pageW - margin;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text(COMPANY.name, cx, y + 3, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(COMPANY.accreditation, cx, y + 7.5, { align: "right" });
  doc.text(COMPANY.address, cx, y + 11.5, { align: "right" });
  doc.text(COMPANY.phone, cx, y + 15.5, { align: "right" });
  doc.text(COMPANY.email, cx, y + 19.5, { align: "right" });

  y += logoH + 7;
  doc.setDrawColor(...C_GREY_TEXT);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_NAVY);
  doc.text("Rectification Quotation", pageW / 2, y, { align: "center" });
  y += 5;
  if (opts.efsm) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_NAVY);
    doc.text(opts.efsm, pageW / 2, y, { align: "center" });
    y += 5;
  }
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(`Reference: RQ-${String(quote.id).padStart(3, "0")}`, pageW / 2, y, { align: "center" });
  y += 7;

  // ── DETAILS BOX ──────────────────────────────────────────────────────────
  doc.setFillColor(...C_GREY_BG);
  doc.roundedRect(margin, y, contentW, 28, 2, 2, "F");
  const col1 = margin + 3;
  const col2 = margin + contentW / 2 + 3;
  const labelH = 4;

  const detailRows = [
    ["Site Name", quote.inspection_site_name || "", "Quote Date", new Date(quote.created_at).toLocaleDateString("en-AU")],
    ["Site Address", quote.inspection_site_address || "", "Inspection Date", quote.inspection_date ? new Date(quote.inspection_date).toLocaleDateString("en-AU") : ""],
    ["Inspection Type", (quote.inspection_type || "").replace("_", "-").replace(/\b\w/g, (c) => c.toUpperCase()), "", ""],
  ];

  let dy = y + 5;
  detailRows.forEach(([l1, v1, l2, v2]) => {
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text(l1.toUpperCase(), col1, dy);
    doc.text(l2.toUpperCase(), col2, dy);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_DARK);
    doc.setFontSize(8.5);
    doc.text(String(v1), col1, dy + labelH);
    doc.text(String(v2), col2, dy + labelH);
    dy += 9;
  });
  y += 32;

  // ── TABLE HEADER ─────────────────────────────────────────────────────────
  checkBreak(12);
  doc.setFillColor(...C_NAVY);
  doc.rect(margin, y, contentW, 7, "F");
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_WHITE);

  const col = {
    num:      margin + 2,
    asset:    margin + 8,
    defect:   margin + 52,
    solution: margin + 92,
    price:    pageW - margin - 2,
  };
  const colW = {
    num: 6, asset: 44, defect: 40, solution: 50, price: 22,
  };

  doc.text("#", col.num, y + 4.5);
  doc.text("Asset / Location", col.asset, y + 4.5);
  doc.text("Defect Description", col.defect, y + 4.5);
  doc.text("Rectification", col.solution, y + 4.5);
  doc.text("Price (ex. GST)", col.price, y + 4.5, { align: "right" });
  y += 9;

  // ── TABLE ROWS ────────────────────────────────────────────────────────────
  const toDataURL = (url) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

  const items = opts.efsm
    ? (quote.items || []).filter((it) => it.inspection_item_detail?.category_name === opts.efsm)
    : (quote.items || []);
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const detail = item.inspection_item_detail || {};
    const assetText = [detail.asset_type_name, detail.floor_level, detail.location].filter(Boolean).join(" · ");
    const failTypeNames = detail.fail_type_names || [];
    const defectText = [
      ...failTypeNames.map((n) => `● ${n}`),
      ...(detail.notes ? [detail.notes] : []),
    ].join("\n");
    const defectLines = doc.splitTextToSize(defectText || "", colW.defect - 2);
    const solutionLines = doc.splitTextToSize(item.proposed_solution || "", colW.solution - 2);
    const assetLines = doc.splitTextToSize(assetText, colW.asset - 2);
    const lineH = 4.5;
    const maxLines = Math.max(defectLines.length, solutionLines.length, assetLines.length);
    const rowH = maxLines * lineH + 3;

    // Photo row height
    const photos = [detail.photo_1_url, detail.photo_2_url, detail.photo_3_url].filter(Boolean);
    const photoH = photos.length > 0 ? 36 : 0;

    checkBreak(rowH + photoH + 4);

    // Alternating row bg
    if (i % 2 === 0) {
      doc.setFillColor(250, 250, 252);
      doc.rect(margin, y, contentW, rowH, "F");
    }

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_DARK);
    doc.text(String(i + 1), col.num, y + lineH);

    doc.setFont("helvetica", "normal");
    doc.text(assetLines, col.asset, y + lineH);
    doc.text(defectLines, col.defect, y + lineH);
    doc.text(solutionLines, col.solution, y + lineH);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_NAVY);
    doc.text(`$${fmt2(item.price_ex_gst)}`, col.price, y + lineH, { align: "right" });

    // Separator
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(margin, y + rowH, margin + contentW, y + rowH);
    y += rowH;

    // Fail photos
    if (photos.length > 0) {
      const imgW = 50;
      const imgH = 32;
      let px = col.asset;
      for (const photoUrl of photos) {
        const dataUrl = await toDataURL(photoUrl);
        if (dataUrl) {
          try {
            doc.addImage(dataUrl, "PNG", px, y + 2, imgW, imgH);
          } catch (e) {}
        }
        px += imgW + 4;
      }
      y += imgH + 6;
    }
  }

  // ── TOTALS ────────────────────────────────────────────────────────────────
  checkBreak(32);
  y += 6;
  const totW = 80;
  const totX = pageW - margin - totW;

  const totalExGst = opts.efsm
    ? items.reduce((sum, it) => sum + (parseFloat(it.price_ex_gst) || 0), 0)
    : Number(quote.total_ex_gst) || 0;
  const gst = opts.efsm ? totalExGst * 0.1 : Number(quote.gst) || 0;
  const totalIncGst = opts.efsm ? totalExGst * 1.1 : Number(quote.total_inc_gst) || 0;

  const totRows = [
    { label: "Subtotal (ex. GST)", value: `$${fmt2(totalExGst)}`, bold: false },
    { label: "GST (10%)", value: `$${fmt2(gst)}`, bold: false },
    { label: "TOTAL (inc. GST)", value: `$${fmt2(totalIncGst)}`, bold: true },
  ];

  totRows.forEach(({ label, value, bold }) => {
    checkBreak(8);
    if (bold) {
      doc.setFillColor(...C_NAVY);
      doc.rect(totX - 2, y - 4, totW + 4, 8, "F");
      doc.setTextColor(...C_WHITE);
    } else {
      doc.setTextColor(...C_DARK);
    }
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 9 : 8);
    doc.text(label, totX, y);
    doc.text(value, pageW - margin, y, { align: "right" });
    y += 8;
  });

  // GST note
  y += 2;
  doc.setFontSize(7);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("All amounts are in Australian Dollars. GST is calculated at 10%.", margin, y);
  y += 8;

  // ── SIGNATURE ─────────────────────────────────────────────────────────────
  const sigW = (contentW - 10) / 3;
  const sigImgH = Math.min(sigW * (211 / 500), 20);
  checkBreak(sigImgH + 14 + 20);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + contentW, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text("Authorised By", margin, y);
  y += 3;

  const sx0 = margin;
  const sx1 = margin + sigW + 5;
  const sx2 = margin + (sigW + 5) * 2;
  const dateStr = new Date().toLocaleDateString("en-AU");
  const sigStartY = y;
  const lineY = sigStartY + sigImgH;

  try {
    doc.addImage("data:image/png;base64," + SIG_B64, "PNG", sx2 + 5, sigStartY, sigW - 5, sigImgH);
  } catch (e) {}

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(sx0, lineY, sx0 + sigW, lineY);
  doc.line(sx1, lineY, sx1 + sigW, lineY);
  doc.line(sx2, lineY, sx2 + sigW, lineY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...C_DARK);
  doc.text(COMPANY.signatory || "", sx0, lineY - 4);
  doc.text(dateStr, sx1, lineY - 4);

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("AUTHORISED SIGNATORY", sx0, lineY + 4);
  doc.text("DATE", sx1, lineY + 4);
  doc.text("SIGNATURE", sx2, lineY + 4);

  y = lineY + 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(COMPANY.name, sx0, y);
  doc.text(COMPANY.accreditation, sx0, y + 4);

  drawPageFooter();

  const siteName = (quote.inspection_site_name || "site").replace(/[^a-z0-9]/gi, "_");
  if (!opts.noDownload) {
    doc.save(`RQ-${String(quote.id).padStart(3, "0")}_${siteName}_Rectification_Quote.pdf`);
  }

  return doc.output("blob");
}
